import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowsClockwise, TrendUp, TrendDown } from "@phosphor-icons/react";
import api from "../lib/api";
import { StreakChip } from "../components/StreakChip";

function Row({ t, idx }) {
  const sos = t.streak_of_streaks || {};
  return (
    <motion.tr
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.015 }}
      className="border-b border-white/5 hover:bg-white/5"
      data-testid="streak-lab-row"
    >
      <td className="py-3 px-3">
        <Link to={`/team/${t.id}`} className="font-head font-bold hover:text-[#007AFF]">{t.name}</Link>
        <span className="text-[10px] font-mono-d text-zinc-600 ml-2">{t.abbr}</span>
      </td>
      <td className="text-zinc-400 px-2 text-xs">{t.league} · {t.division}</td>
      <td className="text-right px-2 font-mono-d tabular-nums text-zinc-300">{t.wins}-{t.losses}</td>
      <td className="text-right px-2">
        <StreakChip type={t.current_streak?.type} length={t.current_streak?.number} blip={t.current_streak?.number === 1} />
      </td>
      <td className="text-right px-3">
        <div className="inline-flex items-center gap-2">
          {sos.type === "W" ? <TrendUp size={14} className="text-[#007AFF]" /> :
           sos.type === "L" ? <TrendDown size={14} className="text-[#FF3B30]" /> : null}
          <span className={`font-display text-2xl ${
            sos.type === "W" ? "text-[#007AFF]" : sos.type === "L" ? "text-[#FF3B30]" : "text-zinc-500"
          }`}>{sos.length || 0}</span>
          <span className="text-[10px] uppercase tracking-wider text-zinc-500">{sos.type || "—"}</span>
        </div>
      </td>
      <td className="text-right px-3 text-xs font-mono-d text-zinc-500 tabular-nums">{t.multi_streaks}·{t.blips}</td>
    </motion.tr>
  );
}

export default function StreakLab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/mlb/streaks-overview");
      setData(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#007AFF] mb-1">Meta · Season {data?.season || ""}</div>
          <h1 className="font-display text-5xl sm:text-6xl leading-none">STREAK LAB</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Ranking all 30 clubs by their <span className="text-white">streak of streaks</span> — consecutive multi-game runs of the same type. Single-game blips are filtered out.
          </p>
        </div>
        <button onClick={load} data-testid="streaklab-refresh-button"
          className="flex items-center gap-2 px-5 py-2.5 bg-transparent border border-white/20 hover:border-white/60 text-white rounded-sm text-xs uppercase tracking-wider font-bold hover:-translate-y-0.5 transition-all">
          <ArrowsClockwise size={16} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {!data ? (
        <div className="text-zinc-500 font-mono-d text-sm" data-testid="streaklab-loading">Computing 30-team meta-streaks… (this can take ~10s)</div>
      ) : (
        <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 border-b border-white/10">
                  <th className="text-left py-3 px-3 font-semibold">Team</th>
                  <th className="text-left px-2 py-3 font-semibold">Div</th>
                  <th className="text-right px-2 py-3 font-semibold">Record</th>
                  <th className="text-right px-2 py-3 font-semibold">Cur Streak</th>
                  <th className="text-right px-3 py-3 font-semibold">Streak of Streaks</th>
                  <th className="text-right px-3 py-3 font-semibold">Multi · Blips</th>
                </tr>
              </thead>
              <tbody>
                {data.teams.map((t, i) => <Row key={t.id} t={t} idx={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
